import { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, MapPin, Truck, AlertCircle, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DeliveryBanner = () => {
  const [settings, setSettings] = useState(null);
  const [postalCodesCount, setPostalCodesCount] = useState(0);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [settingsRes, postalRes] = await Promise.all([
        axios.get(`${API}/delivery/settings`),
        axios.get(`${API}/delivery/postal-codes`).catch(() => ({ data: { codes: [] } })),
      ]);
      setSettings(settingsRes.data);
      setPostalCodesCount((postalRes.data?.codes || []).length);
    } catch (error) {
      console.error('Error fetching delivery settings:', error);
    }
  };

  if (!settings || !settings.delivery_enabled) return null;

  return (
    <div className="bg-[#C62E2E] text-white py-2" data-testid="delivery-banner">
      <div className="max-w-7xl mx-auto px-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs sm:text-sm">
        <div className="flex items-center gap-2">
          <Truck className="w-4 h-4" />
          <span className="font-medium">Livraison</span>
        </div>
        <span className="opacity-60">|</span>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4" />
          <span>{settings.delivery_message}: <strong>
            {settings.delivery_time_minutes}{settings.delivery_time_max_minutes ? `-${settings.delivery_time_max_minutes}` : ''} min
          </strong></span>
        </div>
        {postalCodesCount > 0 && (
          <>
            <span className="opacity-60">|</span>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span><strong>{postalCodesCount}</strong> code{postalCodesCount > 1 ? 's' : ''} postal{postalCodesCount > 1 ? 'aux' : ''} desservi{postalCodesCount > 1 ? 's' : ''}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const AddressChecker = ({ address, onResult }) => {
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (address && address.length > 10) {
      const timer = setTimeout(() => {
        checkAddress(address);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setResult(null);
    }
  }, [address]);

  const checkAddress = async (addr) => {
    setChecking(true);
    try {
      const response = await axios.post(`${API}/delivery/check-address`, { address: addr });
      setResult(response.data);
      if (onResult) onResult(response.data);
    } catch (error) {
      console.error('Error checking address:', error);
      setResult({ in_zone: false, error: 'Erreur de vérification' });
      if (onResult) onResult({ in_zone: false, error: 'Erreur de vérification' });
    } finally {
      setChecking(false);
    }
  };

  if (!address || address.length < 10) return null;

  return (
    <div className="mt-2" data-testid="address-checker">
      {checking ? (
        <div className="flex items-center gap-2 text-[#57534E] text-sm">
          <div className="spinner w-4 h-4 border-2"></div>
          <span>Vérification de l'adresse...</span>
        </div>
      ) : result ? (
        <div className={`flex items-start gap-2 text-sm p-3 rounded-lg ${
          result.in_zone 
            ? 'bg-green-50 text-green-700' 
            : 'bg-red-50 text-red-700'
        }`}>
          {result.in_zone ? (
            <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          )}
          <div>
            <p className="font-medium">
              {result.in_zone ? 'Livraison disponible !' : 'Hors zone de livraison'}
            </p>
            <p className="text-xs mt-1 opacity-80">{result.message}</p>
            {result.address_found && (
              <p className="text-xs mt-1 opacity-60">Adresse trouvée: {result.address_found}</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const DeliveryZoneInfo = () => {
  const [zoneInfo, setZoneInfo] = useState(null);
  const [postalCodes, setPostalCodes] = useState([]);

  useEffect(() => {
    fetchZoneInfo();
  }, []);

  const fetchZoneInfo = async () => {
    try {
      const [zoneRes, postalRes] = await Promise.all([
        axios.get(`${API}/delivery/zone-info`),
        axios.get(`${API}/delivery/postal-codes`).catch(() => ({ data: { codes: [] } })),
      ]);
      setZoneInfo(zoneRes.data);
      setPostalCodes(postalRes.data?.codes || []);
    } catch (error) {
      console.error('Error fetching zone info:', error);
    }
  };

  if (!zoneInfo) return null;

  return (
    <div className="bg-[#FAF9F6] rounded-xl p-6" data-testid="delivery-zone-info">
      <h3 className="font-['Playfair_Display'] text-lg font-semibold mb-4 flex items-center gap-2">
        <Truck className="w-5 h-5 text-[#C62E2E]" />
        Informations livraison
      </h3>
      <ul className="space-y-3 text-sm text-[#57534E]">
        <li className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#C62E2E]" />
          <span>Délai estimé: <strong className="text-[#1C1917]">
            {zoneInfo.delivery_time_minutes}{zoneInfo.delivery_time_max_minutes ? `-${zoneInfo.delivery_time_max_minutes}` : ''} minutes
          </strong></span>
        </li>
        <li className="flex items-center gap-2">
          <span className="w-4 h-4 text-[#C62E2E] font-bold">€</span>
          <span>Commande minimum: <strong className="text-[#1C1917]">{zoneInfo.min_order_amount.toFixed(2)} €</strong></span>
        </li>
        {postalCodes.length > 0 && (
          <li className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-[#C62E2E] mt-0.5" />
            <span>
              Codes postaux desservis:{' '}
              <strong className="text-[#1C1917]">{postalCodes.join(', ')}</strong>
            </span>
          </li>
        )}
      </ul>
    </div>
  );
};

export default DeliveryBanner;
